// Vollea — Admin Invites (generate / copy / revoke / delete)
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBack, IoAdd, IoBanOutline, IoTrashOutline, IoCopyOutline, IoCheckmark } from "react-icons/io5";
import { Btn, Card, Input } from "../../components/ui-vollea";
import { api } from "../../lib/api";
import { C, SP, R } from "../../lib/theme";

export default function AdminInvites() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [count, setCount] = useState("5");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { const data = await api.get("/admin/invites"); setInvites(data || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setMsg("");
    try {
      const data = await api.post("/admin/invites", { count: parseInt(count, 10) || 1, label });
      setMsg(`Generated ${data.created.length} code(s).`);
      setLabel("");
      load();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const revoke = async (code) => { await api.post(`/admin/invites/${code}/revoke`); load(); };
  const remove = async (code) => { await api.del(`/admin/invites/${code}`); load(); };
  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: `${SP.lg}px ${SP.lg}px 90px` }}>
      <button onClick={() => navigate("/admin")} data-testid="invites-back" style={{ color: C.text, marginBottom: SP.md }}>
        <IoChevronBack size={26} />
      </button>
      <h1 style={{ color: C.text, fontWeight: 700, fontSize: 28, margin: 0, letterSpacing: 0.5 }}>Invites</h1>
      <p style={{ color: C.textMuted, fontSize: 14, marginTop: 4 }}>VOL-XXXXXX beta codes</p>

      <Card style={{ marginTop: SP.xl }}>
        <form onSubmit={generate} style={{ display: "flex", flexDirection: "column", gap: SP.md }}>
          <div>
            <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>How many?</div>
            <Input value={count} onChange={(e) => setCount(e.target.value.replace(/\D/g, ""))} testid="invite-count-input" type="number" />
          </div>
          <div>
            <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Label (optional)</div>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} testid="invite-label-input" placeholder="e.g. Reddit cohort" />
          </div>
          <Btn label="Generate" icon={<IoAdd size={18} />} loading={busy} type="submit" testid="invite-generate-button" onClick={generate} />
          {msg ? <div style={{ color: msg.startsWith("Generated") ? C.lime : C.error, fontSize: 13 }}>{msg}</div> : null}
        </form>
      </Card>

      <div style={{ marginTop: SP.xl, display: "flex", flexDirection: "column", gap: SP.sm }} data-testid="invites-list">
        {invites.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: 13, padding: SP.lg, textAlign: "center" }}>No invite codes yet.</div>
        ) : invites.map((i) => (
          <Card key={i.code} testid={`invite-row-${i.code}`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP.sm }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: SP.sm }}>
                  <button onClick={() => copyCode(i.code)} data-testid={`invite-copy-${i.code}`} style={{ color: C.text, fontFamily: "monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {i.code} {copied === i.code ? <IoCheckmark size={14} color={C.lime} /> : <IoCopyOutline size={12} color={C.textDim} />}
                  </button>
                  <StatusChip used={i.used} revoked={i.revoked} />
                </div>
                <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>
                  {i.label || "—"} {i.used_by_name ? ` · used by ${i.used_by_name}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {!i.used && !i.revoked && (
                  <button onClick={() => revoke(i.code)} data-testid={`invite-revoke-${i.code}`} style={{ padding: "8px 10px", borderRadius: R.sm, background: C.surface3, color: C.text, fontSize: 12, fontWeight: 600 }}>
                    <IoBanOutline size={14} />
                  </button>
                )}
                <button onClick={() => remove(i.code)} data-testid={`invite-delete-${i.code}`} style={{ padding: "8px 10px", borderRadius: R.sm, background: C.surface3, color: C.error, fontSize: 12, fontWeight: 600 }}>
                  <IoTrashOutline size={14} />
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function StatusChip({ used, revoked }) {
  const conf = revoked
    ? { bg: C.surface3, fg: C.error, text: "Revoked" }
    : used
      ? { bg: C.surface3, fg: C.textMuted, text: "Used" }
      : { bg: C.limeTint, fg: C.lime, text: "Available" };
  return (
    <span style={{ background: conf.bg, color: conf.fg, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase" }}>
      {conf.text}
    </span>
  );
}
